"use client";

import { SitemapLocation } from "@/app/actions/sitemap-localities";
import Link from "next/link";
import { useState } from "react";

interface LocalitiesListProps {
	city: string;
	localities: SitemapLocation[];
}

const ITEMS_PER_PAGE = 100;

export function LocalitiesList({ city, localities }: LocalitiesListProps) {
	const [currentPage, setCurrentPage] = useState(1);

	const totalPages = Math.ceil(localities.length / ITEMS_PER_PAGE);
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
	const endIndex = startIndex + ITEMS_PER_PAGE;
	const currentLocalities = localities.slice(startIndex, endIndex);

	if (localities.length === 0) {
		return <div className="text-center py-8 text-muted-foreground">No localities available</div>;
	}

	return (
		<div className="space-y-6">
			{/* Localities Grid */}
			<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-1">
				{currentLocalities.map(locality => (
					<Link
						key={locality.id}
						href={`/sitemap/${locality.slug}-in-${city}`}
						className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
					>
						{locality.name}
						<span className="text-xs text-muted-foreground ml-1 opacity-50">
							({locality.subLocations.length} sub-localities)
						</span>
					</Link>
				))}
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex justify-center items-center gap-4 pt-4">
					<button
						onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
						disabled={currentPage === 1}
						className="text-sm text-primary-600 hover:text-primary-700 hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
					>
						← Previous
					</button>
					<span className="text-sm text-muted-foreground">
						{currentPage} / {totalPages}
					</span>
					<button
						onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
						disabled={currentPage === totalPages}
						className="text-sm text-primary-600 hover:text-primary-700 hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
					>
						Next →
					</button>
				</div>
			)}

			{/* Summary */}
			<div className="text-center text-xs text-muted-foreground">
				Showing {startIndex + 1}-{Math.min(endIndex, localities.length)} of {localities.length} localities
			</div>
		</div>
	);
}
